import { HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { ArtifactsDataProvider } from '@trovata/app/features/ai-artifacts/services/artifacts-data-provider';
import { CashArtifactsDataProvider } from '@trovata/app/features/ai-artifacts/services/cash-artifacts-data.provider';
import { TmsArtifactsDataProvider } from '@trovata/app/features/ai-artifacts/services/tms-artifacts-data.provider';
import { ArtifactsState } from '@trovata/app/features/ai-artifacts/store/state/artifacts.state';
import {
	ArtifactDataResponse,
	ArtifactSaveBody,
	ArtifactSaveResult,
	ArtifactsResponse,
	DataArtifact,
	FilePreviewResponse,
	RecordDetailResponse,
} from '@trovata/app/features/ai-artifacts/models/artifacts.model';

/**
 * ArtifactsDataProviderRouter — the app-wide runtime-routing data provider.
 *
 * `ArtifactsState` is a root singleton, so a `/tms` route-level provider override would never reach
 * it. Instead, the root `ARTIFACTS_DATA_PROVIDER` token resolves to THIS router, which dispatches
 * each call to the right backend at runtime:
 *
 * - Active context is TMS → `TmsArtifactsDataProvider` (ATOM `/api/v1/ai-artifact`).
 * - Otherwise (Cash, the default) → `CashArtifactsDataProvider`, an empty no-op (Cash has no
 *   artifact backend).
 *
 * Cash isolation: on any non-TMS context this router is a pure pass-through to the empty Cash
 * provider, so no `/api/v1/ai-artifact` request is ever made on a Cash surface. Mirrors the
 * established `AgentsDataProviderRouter` pattern.
 *
 * Spec lock: `documentation/implementation/system/tms-data-artifacts-migration.md` §3.
 */
@Injectable({ providedIn: 'root' })
export class ArtifactsDataProviderRouter implements ArtifactsDataProvider {
	private readonly router: Router = inject(Router);
	private readonly store: Store = inject(Store);
	private readonly cashProvider: CashArtifactsDataProvider = inject(CashArtifactsDataProvider);
	private readonly tmsProvider: TmsArtifactsDataProvider = inject(TmsArtifactsDataProvider);

	/** Route prefix that identifies the TMS product app. */
	private static readonly TMS_ROUTE_PREFIX: string = '/tms';

	listArtifacts(): Observable<ArtifactsResponse> {
		return this.active().listArtifacts();
	}

	loadArtifact(artifactId: string): Observable<DataArtifact> {
		return this.active().loadArtifact(artifactId);
	}

	getArtifactData(artifactId: string, paramJsonOverride?: Record<string, unknown>): Observable<ArtifactDataResponse> {
		return this.active().getArtifactData(artifactId, paramJsonOverride);
	}

	saveArtifact(body: ArtifactSaveBody): Observable<ArtifactSaveResult> {
		return this.active().saveArtifact(body);
	}

	deleteArtifact(artifactId: string): Observable<void> {
		return this.active().deleteArtifact(artifactId);
	}

	getRecordDetail(artifactId: string, primaryKeys: Record<string, unknown>): Observable<RecordDetailResponse> {
		return this.active().getRecordDetail(artifactId, primaryKeys);
	}

	fileDownload(fileId: string): Observable<HttpResponse<Blob>> {
		return this.active().fileDownload(fileId);
	}

	filePreview(fileId: string): Observable<FilePreviewResponse> {
		return this.active().filePreview(fileId);
	}

	/**
	 * Resolves the provider for the current context. Prefers the EXPLICIT context declared by the active
	 * surface (`ArtifactsState.explicitContext`) — the reliable SSOT — over the `router.url` inference,
	 * because routed components mount before `NavigationEnd` settles the URL, so a URL-only check races on a
	 * hard refresh / SPA nav into `/tms`. Falls back to `router.url` (then Cash) when no context is declared.
	 */
	private active(): ArtifactsDataProvider {
		const explicit: 'cash' | 'tms' | null = this.store.selectSnapshot(ArtifactsState.explicitContext);
		const isTms: boolean = explicit !== null ? explicit === 'tms' : this.router.url.startsWith(ArtifactsDataProviderRouter.TMS_ROUTE_PREFIX);
		return isTms ? this.tmsProvider : this.cashProvider;
	}
}
